import {
  createFileRoute,
  Outlet,
  redirect,
  useRouterState,
} from '@tanstack/react-router'

import { AnnouncementBanner } from '@/components/AnnouncementBanner'
import { AppSidebar } from '@/components/app-sidebar'
import ThemeToggle from '@/components/ThemeToggle'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb'
import {
  SidebarInset,
  SidebarProvider,
  SidebarTrigger,
} from '@/components/ui/sidebar'
import { authContextQuery } from '@/lib/org/queries'

export const Route = createFileRoute('/_authenticated')({
  beforeLoad: async ({ location, context: { queryClient } }) => {
    // Verifies the session AND guarantees the user has an organization
    // (the billing entity) — see getAuthContext. Read through Query rather than
    // calling the server fn directly so hover-preload and the click that follows
    // share one request instead of making two serial ones — see authContextQuery.
    const ctx = await queryClient.ensureQueryData(authContextQuery)
    if (!ctx) {
      throw redirect({ to: '/login', search: { redirect: location.href } })
    }
    // `activeOrg` flows to every child loader so they can prefetch org-scoped
    // queries immediately, instead of each page waiting on its own
    // `useActiveOrganization()` request before its first fetch can start.
    // Null when the session has no active org yet — loaders skip prefetching.
    return { user: ctx.user, activeOrg: ctx.activeOrg }
  },
  component: AppLayout,
})

/**
 * Pathname → breadcrumb (section group + page), mirroring the sidebar's own
 * grouping. Longest matching prefix wins so nested admin routes still resolve.
 * Pages render their own PageHeader inside the content area; this is just the
 * slim chrome crumb next to the sidebar trigger.
 */
const PAGE_META: { prefix: string; group?: string; label: string }[] = [
  { prefix: '/dashboard', group: 'Platform', label: 'Overview' },
  { prefix: '/credentials', group: 'Platform', label: 'Credentials' },
  { prefix: '/usage', group: 'Platform', label: 'Usage' },
  { prefix: '/billing', group: 'Platform', label: 'Billing' },
  { prefix: '/organization', group: 'Organization', label: 'Members' },
  // 'You', not 'Organization': the account page is the person's own profile,
  // independent of the active org. Matches the sidebar grouping.
  { prefix: '/account', group: 'You', label: 'Account' },
  { prefix: '/admin', label: 'Admin' },
]

function crumbForPath(pathname: string): { group?: string; label: string } {
  let match = ''
  for (const { prefix } of PAGE_META) {
    if (pathname.startsWith(prefix) && prefix.length > match.length) {
      match = prefix
    }
  }
  return PAGE_META.find((p) => p.prefix === match) ?? { label: 'Console' }
}

function AppLayout() {
  const pathname = useRouterState({ select: (s) => s.location.pathname })
  const crumb = crumbForPath(pathname)

  return (
    <SidebarProvider>
      <AppSidebar />
      <SidebarInset>
        <header className="sticky top-0 z-10 flex h-14 items-center gap-2 border-b bg-background px-4">
          <SidebarTrigger className="-ml-1" />
          {/* Plain divider: the shadcn Separator's vertical variant keys off a
              `data-vertical` attr that Radix doesn't emit (it uses
              `data-orientation`), so it renders wrong, same fix as the
              marketing pill nav. */}
          <div className="bg-border mr-2 h-4 w-px self-center" />
          <Breadcrumb>
            <BreadcrumbList>
              {crumb.group ? (
                <>
                  {/* Group is context, not a destination, drop it on mobile
                      so the current page always has room. */}
                  <BreadcrumbItem className="hidden sm:block">
                    {crumb.group}
                  </BreadcrumbItem>
                  <BreadcrumbSeparator className="hidden sm:block" />
                </>
              ) : null}
              <BreadcrumbItem>
                <BreadcrumbPage>{crumb.label}</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
          <div className="ml-auto">
            <ThemeToggle />
          </div>
        </header>
        <div className="mx-auto flex w-full max-w-5xl flex-col gap-6 px-4 py-8 lg:px-6">
          <AnnouncementBanner />
          <Outlet />
        </div>
      </SidebarInset>
    </SidebarProvider>
  )
}
